import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  Radio,
  Switch,
  InputNumber,
  message,
  Popconfirm,
  Upload,
  Image,
  Divider,
  Checkbox,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  UploadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { UploadFile } from 'antd/es/upload/interface';

const { Option } = Select;
const { TextArea } = Input;

// 游戏接口
interface VenueGame {
  id: number;
  venueCode: string; // 场馆编码
  venueName: string; // 场馆名称
  gameType: string; // 游戏类型
  gameName: string; // 游戏名称
  gameCode: string; // 游戏代码
  gameRank: number; // 游戏排行
  gameLevel: string; // 游戏等级
  minAmount: number; // 最小金额
  maxAmount: number; // 最大金额
  orientation: 'all' | 'portrait' | 'landscape'; // 横竖屏
  listingStatus: 'listed' | 'delisted'; // 游戏上架状态
  maintenanceStatus: 'normal' | 'maintenance'; // 游戏维护状态
  auditType: string; // 稽核类型
  frontendSort: number; // 前端排序
  onlinePlayerCount: number; // 在线玩家数量
  rtp: number; // RTP
  maxPayoutResult: number; // 最大开奖结果
  gameAttributes: string; // 游戏属性
  isMatched: boolean; // 是否匹配
  isHot: boolean; // 是否热门
  venueListingStatus?: string; // 场馆上架状态
  venueMaintenanceStatus?: string; // 场馆维护状态
  gameImage?: string; // 游戏图片
  icon?: string; // 图标
  thumbnail?: string; // 缩略图
  hotImage?: string; // 热门图
  descriptionImage?: string; // 描述图
  creator: string;
  createTime: string;
  updater: string;
  updateTime: string;
}

// 子游戏映射接口
interface SubGameMapping {
  id: number;
  sourceGameId: number; // 源游戏ID
  sourceGameCode: string; // 源游戏代码
  sourceGameName: string; // 源游戏名称
  targetVenueCode: string; // 目标场馆编码
  targetVenueName: string; // 目标场馆名称
  targetGameCode: string; // 目标游戏代码
  targetGameName: string; // 目标游戏名称
  tagLogic: 'and' | 'or'; // 标签逻辑：且/或
  tagIds: number[]; // 标签ID列表
  tagNames: string[]; // 标签名称列表
  mappingRatio: number; // 映射比例（0-100）
  enabled: boolean; // 是否启用
  creator: string;
  createTime: string;
  updater: string;
  updateTime: string;
}

// 游戏展示配置接口
interface GameDisplayConfig {
  gameId: number; // 游戏ID
  gameCode: string; // 游戏代码
  gameName: string; // 游戏名称
  hiddenTagIds: number[]; // 不展示的标签ID列表
  hiddenTagNames: string[]; // 不展示的标签名称列表
  updater: string;
  updateTime: string;
}

// 模拟数据
const mockVenues = [
  { code: 'JDB', name: 'JDB' },
  { code: 'PG', name: 'PG' },
  { code: 'CQ9', name: 'CQ9' },
  { code: 'PP', name: 'PP' },
  { code: 'EVO', name: 'EVO' },
];

const mockGameTypes = [
  { value: 'fishing', label: '捕鱼' },
  { value: 'slot', label: '老虎机' },
  { value: 'live', label: '真人' },
  { value: 'sports', label: '体育' },
  { value: 'lottery', label: '彩票' },
];

const mockGameLevels = [
  { value: 'level1', label: '一级游戏' },
  { value: 'level2', label: '二级游戏' },
  { value: 'level3', label: '三级游戏' },
];

const mockAuditTypes = [
  { value: 'validBet', label: '有效投注' },
  { value: 'totalBet', label: '总投注' },
  { value: 'winAmount', label: '赢取金额' },
];

// 模拟标签数据（从用户标签管理获取）
const mockTags = [
  { id: 1, name: 'VIP1', color: '#ff4d4f' },
  { id: 2, name: 'VIP2', color: '#ff7a45' },
  { id: 3, name: '活跃用户', color: '#52c41a' },
  { id: 4, name: '沉默用户', color: '#8c8c8c' },
  { id: 5, name: '高价值用户', color: '#1890ff' },
  { id: 6, name: '风险用户', color: '#faad14' },
];

const mockGames: VenueGame[] = [
  {
    id: 1,
    venueCode: 'JDB',
    venueName: 'JDB',
    gameType: 'fishing',
    gameName: '影龙捕鱼',
    gameCode: 'JDB-FISH-009',
    gameRank: 1,
    gameLevel: 'level2',
    minAmount: 0,
    maxAmount: 0,
    orientation: 'all',
    listingStatus: 'listed',
    maintenanceStatus: 'normal',
    auditType: 'validBet',
    frontendSort: 998,
    onlinePlayerCount: 0,
    rtp: 0,
    maxPayoutResult: 0,
    gameAttributes: '["[\"[\\\"[\\\\\\\"[\\\\\\\\\\\\\\\"null\\\\\\\\\\\\\\\"]\\\\\\\"]\\\"]\"]"]',
    isMatched: false,
    isHot: false,
    gameImage: 'https://via.placeholder.com/80x80?text=Game',
    icon: 'https://via.placeholder.com/40x40?text=Icon',
    thumbnail: 'https://via.placeholder.com/60x60?text=Thumb',
    hotImage: 'https://via.placeholder.com/60x60?text=Hot',
    descriptionImage: 'https://via.placeholder.com/60x60?text=Desc',
    creator: 'admin',
    createTime: '2025-11-01 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 2,
    venueCode: 'JDB',
    venueName: 'JDB',
    gameType: 'fishing',
    gameName: 'Super Dragon Fishing',
    gameCode: 'JDB-FISH-001',
    gameRank: 2,
    gameLevel: 'level1',
    minAmount: 1000,
    maxAmount: 100000,
    orientation: 'landscape',
    listingStatus: 'listed',
    maintenanceStatus: 'normal',
    auditType: 'validBet',
    frontendSort: 999,
    onlinePlayerCount: 125,
    rtp: 96.5,
    maxPayoutResult: 5000000,
    gameAttributes: '["fishing","dragon"]',
    isMatched: true,
    isHot: true,
    gameImage: 'https://via.placeholder.com/80x80?text=Super',
    icon: 'https://via.placeholder.com/40x40?text=Icon',
    thumbnail: 'https://via.placeholder.com/60x60?text=Thumb',
    hotImage: 'https://via.placeholder.com/60x60?text=Hot',
    descriptionImage: 'https://via.placeholder.com/60x60?text=Desc',
    creator: 'admin',
    createTime: '2025-11-01 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 3,
    venueCode: 'PG',
    venueName: 'PG',
    gameType: 'slot',
    gameName: 'Lucky 777',
    gameCode: 'PG-SLOT-777',
    gameRank: 3,
    gameLevel: 'level1',
    minAmount: 500,
    maxAmount: 50000,
    orientation: 'portrait',
    listingStatus: 'delisted',
    maintenanceStatus: 'normal',
    auditType: 'totalBet',
    frontendSort: 997,
    onlinePlayerCount: 0,
    rtp: 97.2,
    maxPayoutResult: 10000000,
    gameAttributes: '["slot","lucky"]',
    isMatched: false,
    isHot: false,
    gameImage: 'https://via.placeholder.com/80x80?text=Lucky',
    icon: 'https://via.placeholder.com/40x40?text=Icon',
    thumbnail: 'https://via.placeholder.com/60x60?text=Thumb',
    hotImage: 'https://via.placeholder.com/60x60?text=Hot',
    descriptionImage: 'https://via.placeholder.com/60x60?text=Desc',
    creator: 'admin',
    createTime: '2025-11-01 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-01 10:00:00',
  },
];

const VenueGameManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [mappingForm] = Form.useForm();
  const [games, setGames] = useState<VenueGame[]>(mockGames);
  const [mappings, setMappings] = useState<SubGameMapping[]>([]);
  const [displayConfigs, setDisplayConfigs] = useState<GameDisplayConfig[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [mappingModalVisible, setMappingModalVisible] = useState(false);
  const [displayModalVisible, setDisplayModalVisible] = useState(false);
  const [editingGame, setEditingGame] = useState<VenueGame | null>(null);
  const [mappingGame, setMappingGame] = useState<VenueGame | null>(null);
  const [displayGame, setDisplayGame] = useState<VenueGame | null>(null);
  const [displayForm] = Form.useForm();
  const [filterForm] = Form.useForm();

  // 筛选状态
  const [filters, setFilters] = useState({
    gameName: '',
    gameType: 'all',
    venueCode: 'all',
    gameLevel: 'all',
    listingStatus: 'all',
    maintenanceStatus: 'all',
  });

  // 处理搜索
  const handleSearch = () => {
    const values = filterForm.getFieldsValue();
    setFilters(values);
    message.info('搜索完成');
  };

  // 处理重置
  const handleReset = () => {
    filterForm.resetFields();
    setFilters({
      gameName: '',
      gameType: 'all',
      venueCode: 'all',
      gameLevel: 'all',
      listingStatus: 'all',
      maintenanceStatus: 'all',
    });
  };

  // 处理新增
  const handleAdd = () => {
    setEditingGame(null);
    form.resetFields();
    form.setFieldsValue({
      orientation: 'all',
      listingStatus: 'listed',
      maintenanceStatus: 'normal',
      auditType: 'validBet',
      isMatched: false,
      gameRank: 1,
      frontendSort: 998,
      minAmount: 0,
      maxAmount: 0,
      onlinePlayerCount: 0,
      rtp: 0,
      maxPayoutResult: 0,
      gameAttributes: '',
    });
    setModalVisible(true);
  };

  // 处理编辑
  const handleEdit = (game: VenueGame) => {
    setEditingGame(game);
    form.setFieldsValue({
      ...game,
      gameAttributes: typeof game.gameAttributes === 'string' ? game.gameAttributes : JSON.stringify(game.gameAttributes),
    });
    setModalVisible(true);
  };

  // 处理删除
  const handleDelete = (game: VenueGame) => {
    setGames(prev => prev.filter(item => item.id !== game.id));
    message.success('游戏已删除');
  };

  // 处理提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: VenueGame = {
        id: editingGame ? editingGame.id : Date.now(),
        ...values,
        venueName: mockVenues.find(v => v.code === values.venueCode)?.name || values.venueCode,
        creator: editingGame ? editingGame.creator : 'admin',
        createTime: editingGame ? editingGame.createTime : dayjs().format('YYYY-MM-DD HH:mm:ss'),
        updater: 'admin',
        updateTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      };

      setGames(prev => {
        if (editingGame) {
          return prev.map(item => (item.id === editingGame.id ? payload : item));
        }
        return [payload, ...prev];
      });

      message.success(editingGame ? '游戏已更新' : '游戏已创建');
      setModalVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // 处理子游戏映射
  const handleMapping = (game: VenueGame) => {
    setMappingGame(game);
    mappingForm.resetFields();
    mappingForm.setFieldsValue({
      sourceGameId: game.id,
      sourceGameCode: game.gameCode,
      sourceGameName: game.gameName,
      tagLogic: 'and',
      tagIds: [],
      mappingRatio: 100,
      enabled: true,
    });
    setMappingModalVisible(true);
  };

  // 处理映射提交
  const handleMappingSubmit = async () => {
    try {
      const values = await mappingForm.validateFields();
      const selectedTags = mockTags.filter(tag => values.tagIds.includes(tag.id));
      
      const payload: SubGameMapping = {
        id: Date.now(),
        sourceGameId: values.sourceGameId,
        sourceGameCode: values.sourceGameCode,
        sourceGameName: values.sourceGameName,
        targetVenueCode: values.targetVenueCode,
        targetVenueName: mockVenues.find(v => v.code === values.targetVenueCode)?.name || values.targetVenueCode,
        targetGameCode: values.targetGameCode,
        targetGameName: values.targetGameName,
        tagLogic: values.tagLogic,
        tagIds: values.tagIds,
        tagNames: selectedTags.map(t => t.name),
        mappingRatio: values.mappingRatio,
        enabled: values.enabled,
        creator: 'admin',
        createTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        updater: 'admin',
        updateTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      };

      setMappings(prev => [payload, ...prev]);
      message.success('子游戏映射已创建');
      setMappingModalVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // 获取目标场馆的游戏列表（根据选择的场馆动态加载）
  const getTargetGames = (venueCode: string) => {
    if (!venueCode) return [];
    return games.filter(game => game.venueCode === venueCode);
  };

  // 处理展示配置
  const handleDisplayConfig = (game: VenueGame) => {
    setDisplayGame(game);
    const existingConfig = displayConfigs.find(config => config.gameId === game.id);
    displayForm.resetFields();
    displayForm.setFieldsValue({
      gameId: game.id,
      gameCode: game.gameCode,
      gameName: game.gameName,
      hiddenTagIds: existingConfig?.hiddenTagIds || [],
    });
    setDisplayModalVisible(true);
  };

  // 处理展示配置提交
  const handleDisplayConfigSubmit = async () => {
    try {
      const values = await displayForm.validateFields();
      const selectedTags = mockTags.filter(tag => values.hiddenTagIds.includes(tag.id));
      
      const payload: GameDisplayConfig = {
        gameId: values.gameId,
        gameCode: values.gameCode,
        gameName: values.gameName,
        hiddenTagIds: values.hiddenTagIds,
        hiddenTagNames: selectedTags.map(t => t.name),
        updater: 'admin',
        updateTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      };

      setDisplayConfigs(prev => {
        const existingIndex = prev.findIndex(config => config.gameId === payload.gameId);
        if (existingIndex >= 0) {
          return prev.map((config, index) => index === existingIndex ? payload : config);
        }
        return [payload, ...prev];
      });

      message.success('展示配置已保存');
      setDisplayModalVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // 获取游戏的展示配置
  const getGameDisplayConfig = (gameId: number): GameDisplayConfig | undefined => {
    return displayConfigs.find(config => config.gameId === gameId);
  };

  // 筛选后的游戏列表
  const filteredGames = games.filter(game => {
    if (filters.gameName && !game.gameName.toLowerCase().includes(filters.gameName.toLowerCase())) {
      return false;
    }
    if (filters.gameType !== 'all' && game.gameType !== filters.gameType) {
      return false;
    }
    if (filters.venueCode !== 'all' && game.venueCode !== filters.venueCode) {
      return false;
    }
    if (filters.gameLevel !== 'all' && game.gameLevel !== filters.gameLevel) {
      return false;
    }
    if (filters.listingStatus !== 'all' && game.listingStatus !== filters.listingStatus) {
      return false;
    }
    if (filters.maintenanceStatus !== 'all' && game.maintenanceStatus !== filters.maintenanceStatus) {
      return false;
    }
    return true;
  });

  const columns = [
    {
      title: '游戏属性',
      dataIndex: 'gameAttributes',
      key: 'gameAttributes',
      width: 200,
      ellipsis: true,
      render: (text: string) => (
        <span style={{ fontSize: '12px' }}>{text}</span>
      ),
    },
    {
      title: '游戏排行',
      dataIndex: 'gameRank',
      key: 'gameRank',
      width: 100,
      sorter: (a: VenueGame, b: VenueGame) => a.gameRank - b.gameRank,
    },
    {
      title: '是否匹配',
      dataIndex: 'isMatched',
      key: 'isMatched',
      width: 100,
      render: (isMatched: boolean) => (
        <Tag color={isMatched ? 'success' : 'error'}>
          {isMatched ? '是' : '否'}
        </Tag>
      ),
    },
    {
      title: '是否热门',
      dataIndex: 'isHot',
      key: 'isHot',
      width: 100,
      render: (isHot: boolean) => (
        <Tag color={isHot ? 'success' : 'error'}>
          {isHot ? '是' : '否'}
        </Tag>
      ),
    },
    {
      title: '场馆上架状态',
      dataIndex: 'venueListingStatus',
      key: 'venueListingStatus',
      width: 120,
      render: (text: string) => text || '--',
    },
    {
      title: '场馆维护状态',
      dataIndex: 'venueMaintenanceStatus',
      key: 'venueMaintenanceStatus',
      width: 120,
      render: (text: string) => text || '--',
    },
    {
      title: '游戏维护状态',
      dataIndex: 'maintenanceStatus',
      key: 'maintenanceStatus',
      width: 120,
      render: (status: string) => (
        <Tag color={status === 'normal' ? 'success' : 'warning'}>
          {status === 'normal' ? '正常' : '维护中'}
        </Tag>
      ),
    },
    {
      title: '游戏上架状态',
      dataIndex: 'listingStatus',
      key: 'listingStatus',
      width: 120,
      render: (status: string) => (
        <Tag color={status === 'listed' ? 'success' : 'error'}>
          {status === 'listed' ? '上架' : '下架'}
        </Tag>
      ),
    },
    {
      title: '游戏图片',
      dataIndex: 'gameImage',
      key: 'gameImage',
      width: 100,
      render: (url: string) => url ? (
        <Image src={url} alt="游戏图片" width={80} height={80} style={{ objectFit: 'cover' }} />
      ) : '--',
    },
    {
      title: '游戏代码',
      dataIndex: 'gameCode',
      key: 'gameCode',
      width: 150,
    },
    {
      title: '图标',
      dataIndex: 'icon',
      key: 'icon',
      width: 80,
      render: (url: string) => url ? (
        <Image src={url} alt="图标" width={40} height={40} style={{ objectFit: 'cover' }} />
      ) : '--',
    },
    {
      title: '缩略图',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      width: 80,
      render: (url: string) => url ? (
        <Image src={url} alt="缩略图" width={60} height={60} style={{ objectFit: 'cover' }} />
      ) : '--',
    },
    {
      title: '热门图',
      dataIndex: 'hotImage',
      key: 'hotImage',
      width: 80,
      render: (url: string) => url ? (
        <Image src={url} alt="热门图" width={60} height={60} style={{ objectFit: 'cover' }} />
      ) : '--',
    },
    {
      title: '描述图',
      dataIndex: 'descriptionImage',
      key: 'descriptionImage',
      width: 80,
      render: (url: string) => url ? (
        <Image src={url} alt="描述图" width={60} height={60} style={{ objectFit: 'cover' }} />
      ) : '--',
    },
    {
      title: '场馆编码',
      dataIndex: 'venueCode',
      key: 'venueCode',
      width: 100,
    },
    {
      title: '映射',
      key: 'mapping',
      width: 120,
      render: (_: any, record: VenueGame) => {
        const mapping = mappings.find(m => m.sourceGameId === record.id);
        return mapping ? (
          <Tag color="blue">已配置</Tag>
        ) : (
          <Tag color="default">未配置</Tag>
        );
      },
    },
    {
      title: '展示',
      key: 'display',
      width: 120,
      render: (_: any, record: VenueGame) => {
        const config = getGameDisplayConfig(record.id);
        if (config && config.hiddenTagIds.length > 0) {
          return (
            <Tooltip title={`对以下标签不展示：${config.hiddenTagNames.join(', ')}`}>
              <Tag color="orange">已配置 ({config.hiddenTagIds.length}个标签)</Tag>
            </Tooltip>
          );
        }
        return <Tag color="default">未配置</Tag>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 250,
      fixed: 'right' as const,
      render: (_: any, record: VenueGame) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button type="link" icon={<LinkOutlined />} onClick={() => handleMapping(record)}>
            映射
          </Button>
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleDisplayConfig(record)}>
            展示
          </Button>
          <Popconfirm
            title="确认删除该游戏？"
            onConfirm={() => handleDelete(record)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const targetVenueCode = Form.useWatch('targetVenueCode', mappingForm);

  return (
    <div style={{ padding: 24 }}>
      <Card>
        {/* 搜索筛选区域 */}
        <Form form={filterForm} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="gameName" style={{ marginBottom: 16 }}>
            <Input
              placeholder="请输入游戏名称"
              prefix={<SearchOutlined />}
              allowClear
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item name="gameType" initialValue="all" style={{ marginBottom: 16 }}>
            <Select style={{ width: 150 }}>
              <Option value="all">全部</Option>
              {mockGameTypes.map(type => (
                <Option key={type.value} value={type.value}>{type.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="venueCode" initialValue="all" style={{ marginBottom: 16 }}>
            <Select style={{ width: 150 }}>
              <Option value="all">全部</Option>
              {mockVenues.map(venue => (
                <Option key={venue.code} value={venue.code}>{venue.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="gameLevel" initialValue="all" style={{ marginBottom: 16 }}>
            <Select style={{ width: 150 }}>
              <Option value="all">全部</Option>
              {mockGameLevels.map(level => (
                <Option key={level.value} value={level.value}>{level.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="listingStatus" initialValue="all" style={{ marginBottom: 16 }}>
            <Select style={{ width: 150 }}>
              <Option value="all">全部</Option>
              <Option value="listed">上架</Option>
              <Option value="delisted">下架</Option>
            </Select>
          </Form.Item>
          <Form.Item name="maintenanceStatus" initialValue="all" style={{ marginBottom: 16 }}>
            <Select style={{ width: 150 }}>
              <Option value="all">全部</Option>
              <Option value="normal">正常</Option>
              <Option value="maintenance">维护中</Option>
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 16 }}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                搜索
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                新增
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={filteredGames}
          rowKey="id"
          scroll={{ x: 2000 }}
          pagination={{
            total: filteredGames.length,
            pageSize: 50,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Card>

      {/* 新增/编辑游戏模态框 */}
      <Modal
        title={editingGame ? '修改场馆游戏' : '新增场馆游戏'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        width={800}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="场馆"
                name="venueCode"
                rules={[{ required: true, message: '请选择场馆' }]}
              >
                <Select placeholder="请选择场馆">
                  {mockVenues.map(venue => (
                    <Option key={venue.code} value={venue.code}>{venue.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="游戏类型"
                name="gameType"
                rules={[{ required: true, message: '请选择游戏类型' }]}
              >
                <Select placeholder="请选择游戏类型">
                  {mockGameTypes.map(type => (
                    <Option key={type.value} value={type.value}>{type.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="游戏名称"
                name="gameName"
                rules={[{ required: true, message: '请输入游戏名称' }]}
              >
                <Input placeholder="请输入游戏名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="游戏代码"
                name="gameCode"
                rules={[{ required: true, message: '请输入游戏代码' }]}
              >
                <Input placeholder="请输入游戏代码" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="游戏排行"
                name="gameRank"
                rules={[{ required: true, message: '请输入游戏排行' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="游戏等级"
                name="gameLevel"
                rules={[{ required: true, message: '请选择游戏等级' }]}
              >
                <Select placeholder="请选择游戏等级">
                  {mockGameLevels.map(level => (
                    <Option key={level.value} value={level.value}>{level.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="最小金额"
                name="minAmount"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="最大金额"
                name="maxAmount"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="横竖屏"
            name="orientation"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio value="all">全部</Radio>
              <Radio value="portrait">竖屏</Radio>
              <Radio value="landscape">横屏</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label="游戏上架状态"
            name="listingStatus"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio value="listed">上架</Radio>
              <Radio value="delisted">下架</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label="游戏维护状态"
            name="maintenanceStatus"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio value="normal">正常</Radio>
              <Radio value="maintenance">维护中</Radio>
            </Radio.Group>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="稽核类型"
                name="auditType"
                rules={[{ required: true, message: '请选择稽核类型' }]}
              >
                <Select placeholder="请选择稽核类型">
                  {mockAuditTypes.map(type => (
                    <Option key={type.value} value={type.value}>{type.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="前端排序"
                name="frontendSort"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="在线玩家数量"
                name="onlinePlayerCount"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="RTP"
                name="rtp"
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="最大开奖结果"
                name="maxPayoutResult"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="是否匹配"
                name="isMatched"
                valuePropName="checked"
              >
                <Switch checkedChildren="是" unCheckedChildren="否" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="游戏属性"
            name="gameAttributes"
            extra="多个请用英文逗号隔开"
          >
            <TextArea rows={3} placeholder="请输入游戏属性" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 子游戏映射模态框 */}
      <Modal
        title="子游戏映射"
        open={mappingModalVisible}
        onCancel={() => setMappingModalVisible(false)}
        onOk={handleMappingSubmit}
        width={800}
      >
        <Form
          form={mappingForm}
          layout="vertical"
        >
          <Form.Item label="源游戏">
            <Input.Group compact>
              <Input
                style={{ width: '30%' }}
                value={mappingGame?.gameCode}
                disabled
                addonBefore="游戏代码"
              />
              <Input
                style={{ width: '70%' }}
                value={mappingGame?.gameName}
                disabled
                addonBefore="游戏名称"
              />
            </Input.Group>
          </Form.Item>

          <Divider orientation="left">目标游戏配置</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="目标场馆"
                name="targetVenueCode"
                rules={[{ required: true, message: '请选择目标场馆' }]}
              >
                <Select placeholder="请选择目标场馆">
                  {mockVenues.map(venue => (
                    <Option key={venue.code} value={venue.code}>{venue.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="目标子游戏"
                name="targetGameCode"
                rules={[{ required: true, message: '请选择目标子游戏' }]}
              >
                <Select
                  placeholder="请选择目标子游戏"
                  disabled={!targetVenueCode}
                  showSearch
                  optionFilterProp="children"
                  onChange={(value) => {
                    const selectedGame = getTargetGames(targetVenueCode || '').find(g => g.gameCode === value);
                    if (selectedGame) {
                      mappingForm.setFieldsValue({
                        targetGameName: selectedGame.gameName,
                      });
                    }
                  }}
                >
                  {getTargetGames(targetVenueCode || '').map(game => (
                    <Option key={game.id} value={game.gameCode}>
                      {game.gameName} ({game.gameCode})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">映射范围配置</Divider>
          <Form.Item
            label="标签逻辑"
            name="tagLogic"
            rules={[{ required: true }]}
            extra="选择标签之间的逻辑关系"
          >
            <Radio.Group>
              <Radio value="and">且（同时满足所有标签）</Radio>
              <Radio value="or">或（满足任一标签）</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="选择标签"
            name="tagIds"
            rules={[
              { required: true, message: '请至少选择一个标签' },
              { type: 'array', min: 1, message: '请至少选择一个标签' },
              { type: 'array', max: 2, message: '最多选择两个标签' },
            ]}
            extra="最多选择两个标签，用于配置映射范围"
          >
            <Select
              mode="multiple"
              placeholder="请选择标签（最多2个）"
              maxTagCount={2}
              showSearch
              optionFilterProp="children"
            >
              {mockTags.map(tag => (
                <Option key={tag.id} value={tag.id}>
                  <Tag color={tag.color}>{tag.name}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Divider orientation="left">映射比例配置</Divider>
          <Form.Item
            label="映射比例"
            name="mappingRatio"
            rules={[
              { required: true, message: '请输入映射比例' },
              { type: 'number', min: 0, max: 100, message: '映射比例必须在0-100之间' },
            ]}
            extra="符合标签条件的用户中，有多少比例会直接映射到目标子游戏（0-100%）"
          >
            <InputNumber
              min={0}
              max={100}
              style={{ width: '100%' }}
              addonAfter="%"
              placeholder="请输入映射比例"
            />
          </Form.Item>

          <Form.Item
            label="启用状态"
            name="enabled"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item name="sourceGameId" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="sourceGameCode" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="sourceGameName" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="targetGameName" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* 展示配置模态框 */}
      <Modal
        title="展示配置"
        open={displayModalVisible}
        onCancel={() => setDisplayModalVisible(false)}
        onOk={handleDisplayConfigSubmit}
        width={600}
        okText="保存"
        cancelText="取消"
      >
        <Form
          form={displayForm}
          layout="vertical"
        >
          <Form.Item label="游戏信息">
            <Input.Group compact>
              <Input
                style={{ width: '30%' }}
                value={displayGame?.gameCode}
                disabled
                addonBefore="游戏代码"
              />
              <Input
                style={{ width: '70%' }}
                value={displayGame?.gameName}
                disabled
                addonBefore="游戏名称"
              />
            </Input.Group>
          </Form.Item>

          <Divider orientation="left">展示配置</Divider>
          <Form.Item
            label="选择标签（不展示）"
            name="hiddenTagIds"
            extra="选择标签后，拥有该标签的用户在客户端将无法看到此游戏（包括大奖播报、热门游戏、游戏列表中的此游戏）"
          >
            <Select
              mode="multiple"
              placeholder="请选择标签（可多选）"
              showSearch
              optionFilterProp="children"
              maxTagCount="responsive"
            >
              {mockTags.map(tag => (
                <Option key={tag.id} value={tag.id}>
                  <Tag color={tag.color}>{tag.name}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          {displayGame && (() => {
            const config = getGameDisplayConfig(displayGame.id);
            if (config && config.hiddenTagIds.length > 0) {
              return (
                <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>当前配置：</div>
                  <div>
                    {config.hiddenTagNames.map((name, index) => {
                      const tag = mockTags.find(t => t.name === name);
                      return (
                        <Tag key={index} color={tag?.color} style={{ marginBottom: 4 }}>
                          {name}
                        </Tag>
                      );
                    })}
                  </div>
                </div>
              );
            }
            return null;
          })()}

          <div style={{ marginTop: 16, padding: 12, background: '#fff7e6', borderRadius: 4, border: '1px solid #ffd591' }}>
            <div style={{ color: '#d46b08', fontWeight: 500, marginBottom: 4 }}>说明：</div>
            <div style={{ color: '#d46b08', fontSize: 12 }}>
              配置后，拥有所选标签的用户在客户端将无法看到此游戏（包括大奖播报、热门游戏、游戏列表中的此游戏）。
            </div>
          </div>

          <Form.Item name="gameId" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="gameCode" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="gameName" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VenueGameManagement;

